import type { AxiosInstance } from "axios";
import type {
  AddMemberInput,
  DraftResourceAssignment,
  GroupAssignment,
  GroupMember,
} from "../types";
import {
  addAssignments,
  addMembers,
  listAssignments,
  listMembers,
  removeAssignment,
  removeMember,
} from "./request";

export function groupAssignmentsToDraft(
  rows: GroupAssignment[]
): DraftResourceAssignment[] {
  return rows.map((a) => ({
    resourceType: a.resourceType,
    resourceId: a.resourceId,
    eventTypes: a.eventTypes,
    resourceDisplayHint: a.resourceDisplayHint ?? null,
    resourceSubtitle: a.resourceSubtitle ?? null,
  }));
}

/** Strip UI-only fields before calling the notifications API. */
export function draftsToAssignmentApiPayload(
  rows: DraftResourceAssignment[]
): Array<{
  resourceType: string;
  resourceId: string;
  eventTypes: string[] | null;
}> {
  return rows.map((r) => ({
    resourceType: r.resourceType,
    resourceId: r.resourceId,
    eventTypes: r.eventTypes,
  }));
}

export function groupMembersToDraftInputs(
  members: GroupMember[]
): AddMemberInput[] {
  return members.map((m) => {
    if (m.memberType === "user" && m.auth0Id) {
      return {
        type: "user" as const,
        auth0Id: m.auth0Id,
        email: m.email,
        displayName: m.displayName ?? undefined,
      };
    }
    return {
      type: "email" as const,
      email: m.email,
      displayName: m.displayName ?? undefined,
    };
  });
}

function assignmentKey(d: DraftResourceAssignment | GroupAssignment): string {
  return `${d.resourceType}::${d.resourceId}`;
}

function draftMemberKey(m: AddMemberInput): string {
  if (m.type === "user") return `u:${m.auth0Id}`;
  return `e:${m.email.toLowerCase()}`;
}

function serverMemberKey(m: GroupMember): string {
  if (m.memberType === "user" && m.auth0Id) return `u:${m.auth0Id}`;
  return `e:${m.email.toLowerCase()}`;
}

export async function persistAssignmentChanges(
  api: AxiosInstance,
  tenantId: number,
  groupId: string,
  draft: DraftResourceAssignment[],
  previousServer: GroupAssignment[]
): Promise<void> {
  const desired = new Set(draft.map(assignmentKey));
  for (const a of previousServer) {
    if (!desired.has(assignmentKey(a))) {
      await removeAssignment(api, tenantId, groupId, a.id);
    }
  }
  const prevKeys = new Set(previousServer.map((a) => assignmentKey(a)));
  const additions = draft.filter((d) => !prevKeys.has(assignmentKey(d)));
  if (additions.length > 0) {
    await addAssignments(
      api,
      tenantId,
      groupId,
      draftsToAssignmentApiPayload(additions)
    );
  }
}

export async function persistMemberChanges(
  api: AxiosInstance,
  tenantId: number,
  groupId: string,
  draft: AddMemberInput[],
  previousServer: GroupMember[]
): Promise<void> {
  const desired = new Set(draft.map(draftMemberKey));
  for (const m of previousServer) {
    if (!desired.has(serverMemberKey(m))) {
      await removeMember(api, tenantId, groupId, m.id);
    }
  }
  const prevKeys = new Set(previousServer.map(serverMemberKey));
  const additions = draft.filter((d) => !prevKeys.has(draftMemberKey(d)));
  if (additions.length > 0) {
    await addMembers(api, tenantId, groupId, additions);
  }
}

/** Loads current server rows immediately before applying diffs (avoids stale snapshots). */
export async function fetchCurrentAssignmentsAndMembers(
  api: AxiosInstance,
  tenantId: number,
  groupId: string
): Promise<{
  assignments: GroupAssignment[];
  members: GroupMember[];
}> {
  const [asRes, memRes] = await Promise.all([
    listAssignments(api, tenantId, groupId),
    listMembers(api, tenantId, groupId),
  ]);
  return {
    assignments: asRes.data.data,
    members: memRes.data.data,
  };
}
